import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ErrorText } from './content-fields'
import type { Translate } from './content-types'

/**
 * Props for {@link ContentConfirmDialogView}.
 *
 * @property lines - Extra statements under the body (e.g. the publish schedule and replacement).
 * @property error - A server refusal shown inside the still-open dialog.
 * @property cancelLabel - Overrides the default "Cancel" label (e.g. "Stay").
 */
export interface ContentConfirmDialogViewProps {
	t: Translate
	isOpen: boolean
	title: string
	body: string
	lines?: string[]
	confirmLabel: string
	cancelLabel?: string
	danger?: boolean
	isBusy?: boolean
	error?: string | null
	onConfirm: () => void
	onCancel: () => void
}

/** A confirm/cancel dialog for the content screens' guarded actions (organism). */
export function ContentConfirmDialogView(props: ContentConfirmDialogViewProps): ReactElement {
	const { t } = props
	return (
		<Dialog
			isOpen={props.isOpen}
			onOpenChange={(open) => (open ? undefined : props.onCancel())}
			title={props.title}
			labels={{ close: t('actions.close') }}
		>
			<p className="text-ds-sm text-ds-text">{props.body}</p>
			{props.lines?.map((line, i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: lines are positional and may repeat
				<p key={i} className="text-ds-sm font-medium text-ds-text">
					{line}
				</p>
			))}
			<ErrorText message={props.error} />
			<div className="mt-3 flex gap-2">
				<Button variant={props.danger ? 'danger' : 'primary'} isDisabled={props.isBusy} onPress={props.onConfirm}>
					{props.confirmLabel}
				</Button>
				<Button variant="muted" onPress={props.onCancel}>
					{props.cancelLabel ?? t('actions.cancel')}
				</Button>
			</div>
		</Dialog>
	)
}
